import React, { ReactNode, useMemo } from 'react';
import { NavigationConfig } from './types';
import { useSidebarStore } from '../../../stores/sidebar-store';
import { cn } from '../../../utils';
import { TooltipProvider } from '../../ui/tooltip';
import { useSidebarStyles } from './use-sidebar-styles';
import { AppSidebarSection } from './app-sidebar-section';
import { AppSidebarBottomSection } from './app-sidebar-bottom-section';
import { AppSidebarItem } from './app-sidebar-item';
import { AppSidebarToggler } from './app-sidebar-toggler';
import { isRouteActive } from './utils/route-matcher';
import { UILogo } from '../../ui/ui-logo';
import {
  ANIMATION_DURATION,
  BRAND_COLORS,
  SIDEBAR_DIMENSIONS,
} from './constants';

interface AppSidebarProps {
  config: NavigationConfig;
  className?: string;
  logo?: ReactNode;
  logoHref?: string;
  collapseButton?: ReactNode | null;
  pathname: string;
  onNavigate?: (href: string, isExternal: boolean) => void;
}

export function AppSidebar({
  config,
  className,
  logo,
  logoHref,
  collapseButton,
  pathname,
  onNavigate,
}: AppSidebarProps) {
  const { isCollapsed } = useSidebarStore();
  const styles = useSidebarStyles(isCollapsed);

  const logoSection = logo ? (
    <div
      className="tw-flex-shrink-0 tw-border-b tw-flex tw-items-center tw-relative"
      style={{
        ...styles.logoSection,
        borderBottomWidth: '1px',
        borderBottomColor: BRAND_COLORS.PRUSSIAN,
        borderBottomStyle: 'solid',
        boxSizing: 'border-box' as const,
      }}
    >
      {/* The divider between the logo and the topbar, inset 12px top and
          bottom. It stands in for the sidebar's right border in the band. */}
      <span
        aria-hidden
        className="tw-absolute tw-right-0 tw-w-px"
        style={{
          top: '12px',
          bottom: '12px',
          backgroundColor: BRAND_COLORS.PRUSSIAN,
        }}
      />
      <UILogo
        logo={logo}
        href={logoHref}
        isCollapsed={isCollapsed}
        className="tw-flex-shrink-0"
      />
    </div>
  ) : null;

  const sectionsAndStandalone = useMemo(
    () => (
      <div
        className="tw-flex tw-flex-col tw-flex-1"
        style={styles.sectionsContainer}
      >
        {config.standaloneLinks && config.standaloneLinks.length > 0 && (
          <div
            className="tw-w-full"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginTop: isCollapsed ? '4px' : '24px',
              transition: `margin-top ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            }}
          >
            {config.standaloneLinks.map((item) => {
              const isActive = isRouteActive(
                pathname,
                item.href,
                item.activeMatcher
              );
              return (
                <AppSidebarItem
                  key={item.href}
                  item={item}
                  isActive={isActive}
                  isCollapsed={isCollapsed}
                  onNavigate={onNavigate}
                />
              );
            })}
          </div>
        )}
        {config.sections.map((section, index) => (
          <AppSidebarSection
            key={index}
            section={section}
            isCollapsed={isCollapsed}
            pathname={pathname}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    ),
    [
      config.sections,
      config.standaloneLinks,
      isCollapsed,
      pathname,
      styles.sectionsContainer,
      onNavigate,
    ]
  );

  const bottomLinks = useMemo(() => {
    if (!config.bottomLinks || config.bottomLinks.length === 0) return null;
    return (
      <AppSidebarBottomSection
        items={config.bottomLinks}
        isCollapsed={isCollapsed}
        pathname={pathname}
        onNavigate={onNavigate}
      />
    );
  }, [config.bottomLinks, isCollapsed, pathname, onNavigate]);

  const footer =
    collapseButton !== null ? (
      <div
        className="tw-flex-shrink-0 tw-border-t"
        style={{
          ...styles.footer,
          borderTopColor: '#e5e7eb',
          padding: '12px',
        }}
      >
        {collapseButton || <AppSidebarToggler />}
      </div>
    ) : null;

  return (
    <TooltipProvider delayDuration={100}>
      <aside
        className={cn(
          'tw-relative tw-flex tw-flex-col tw-bg-white tw-text-gray-900 tw-box-border tw-overflow-x-hidden tw-flex-shrink-0 tw-h-full',
          'new-admin-sidebar', // Add unique class to prevent old CSS conflicts
          className
        )}
        style={styles.sidebar}
      >
        {/* The right border, starting below the logo band where the prussian
            divider takes over. */}
        <span
          aria-hidden
          className="tw-absolute tw-right-0 tw-bottom-0 tw-w-px tw-pointer-events-none"
          style={{
            top: SIDEBAR_DIMENSIONS.LOGO_SECTION_HEIGHT,
            backgroundColor: '#e5e7eb',
          }}
        />
        <div
          className="tw-h-full tw-flex tw-flex-col"
          style={{
            ...styles.container,
            overflowY: 'hidden',
            overflowX: 'visible',
          }}
        >
          {logoSection}
          <nav
            className="tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-flex tw-flex-col"
            style={{
              ...styles.nav,
              paddingLeft: '12px',
              paddingRight: '12px',
            }}
          >
            {sectionsAndStandalone}
            {bottomLinks}
          </nav>
          {footer}
        </div>
      </aside>
    </TooltipProvider>
  );
}
